import api from './client'

export type HouseholdAccessRole = 'MEMBER' | 'VIEWER'

export interface UserMember {
  id: string
  userId: string
  displayName: string | null
  email: string
  avatarPreset: string
  role: 'OWNER' | 'MEMBER' | 'VIEWER'
  joinedAt: string
}

export interface TrackedPerson {
  id: string
  name: string
  joinedAt: string
}

export interface PendingInvite {
  id: string
  invitedEmail: string
  status: string
  expiresAt: string
  trackedPersonMemberId: string | null
  role: 'MEMBER' | 'VIEWER'
}

export interface MembersResponse {
  users: UserMember[]
  trackedPeople: TrackedPerson[]
}

export function listMembers(): Promise<MembersResponse> {
  return api.get('/household/members').then((r) => r.data)
}

export function addTrackedPerson(name: string): Promise<TrackedPerson> {
  return api.post('/household/members', { name }).then((r) => r.data)
}

export function renameTrackedPerson(id: string, name: string): Promise<TrackedPerson> {
  return api.patch(`/household/members/${id}`, { name }).then((r) => r.data)
}

export function removeTrackedPerson(id: string): Promise<void> {
  return api.delete(`/household/members/${id}`)
}

export function removeUserMember(id: string): Promise<void> {
  return api.delete(`/household/members/${id}`)
}

export function transferOwnership(id: string): Promise<UserMember> {
  return api.patch(`/household/members/${id}/role`).then((r) => r.data)
}

/** Owner-only. Toggles a user member between full read-write (MEMBER) and read-only (VIEWER) —
 *  never OWNER, which only changes via transferOwnership. */
export function updateMemberAccess(id: string, role: HouseholdAccessRole): Promise<UserMember> {
  return api.patch(`/household/members/${id}/access`, { role }).then((r) => r.data)
}

export function listPendingInvites(): Promise<{ invites: PendingInvite[] }> {
  return api.get('/household/invites').then((r) => r.data)
}

export function inviteUser(
  email: string,
  trackedPersonMemberId?: string,
  role?: HouseholdAccessRole
): Promise<PendingInvite> {
  return api.post('/household/invites', { email, trackedPersonMemberId, role }).then((r) => r.data)
}

export function revokeInvite(id: string): Promise<void> {
  return api.delete(`/household/invites/${id}`)
}

/** Re-sends the same accept link (token unchanged) and refreshes the 7-day expiry. Rate-limited
 *  server-side — a small number of resends per invite per hour. */
export function resendInvite(id: string): Promise<PendingInvite> {
  return api.post(`/household/invites/${id}/resend`).then((r) => r.data)
}

export function acceptInvite(token: string): Promise<{ householdId: string }> {
  return api.post('/household/invites/accept', { token }).then((r) => r.data)
}

export interface InvitePreview {
  householdName: string
  inviterName: string
  invitedEmail: string
}

/** Public — no sign-in required. Lets a not-yet-signed-in invitee see what/who they're joining
 *  before they sign up. */
export function previewInvite(token: string): Promise<InvitePreview> {
  return api.get('/household/invites/preview', { params: { token } }).then((r) => r.data)
}
